import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogIn, LogOut } from "lucide-react";
import { COMPANY } from "../lib/data";
import { useAuth } from "../lib/AuthContext";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/about", label: "People & Culture" },
    { to: "/contact", label: "Contact" },
    { to: "https://www.mwcplanroom.com/", label: "Plan Room", external: true },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { user, signIn, signOut } = useAuth() || {};

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-50 bg-foreground/95 backdrop-blur-sm border-b border-foreground"
        >
            <div className="px-6 md:px-12 lg:px-24">
                <div className="flex items-center justify-between h-20">
                    <Link
                        to="/"
                        data-testid="navbar-logo-link"
                        className="flex items-center gap-3 group"
                    >
                        <img
                            src="https://static.wixstatic.com/media/4195cb_a4f68f05b54f4b9089f7ad6655330a96~mv2.png"
                            alt="Mark Wilson Construction"
                            data-testid="navbar-logo-img"
                            className="h-12 w-auto object-contain max-w-[110px]"
                        />
                        <div className="hidden sm:flex flex-col leading-tight">
                            <span className="font-display font-black text-sm tracking-tight uppercase text-background">
                                Mark Wilson
                            </span>
                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-background/60">
                                Construction · {COMPANY.founded}
                            </span>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV_LINKS.map((link) => (
                            link.external ? (
                                <a
                                    key={link.to}
                                    href={link.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                    className="px-4 py-2 text-sm font-medium uppercase tracking-wider text-background/65 hover:text-background border-b-2 border-transparent transition-colors"
                                >
                                    {link.label}
                                </a>
                            ) : (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    end={link.to === "/"}
                                    data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                    className={({ isActive }) =>
                                        `px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                                            isActive
                                                ? "text-background border-b-2 border-accent"
                                                : "text-background/65 hover:text-background border-b-2 border-transparent"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            )
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        {user ? (
                            <div className="relative">
                                <button
                                    data-testid="navbar-user-btn"
                                    onClick={() => setMenuOpen((v) => !v)}
                                    className="flex items-center gap-2 pl-1 pr-3 py-1 border border-background/20 hover:border-accent transition-colors"
                                >
                                    {user.picture ? (
                                        <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground grid place-items-center font-bold text-xs">
                                            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-background/85 max-w-[120px] truncate">
                                        {user.name || user.email}
                                    </span>
                                </button>
                                {menuOpen && (
                                    <div
                                        data-testid="navbar-user-menu"
                                        className="absolute right-0 top-full mt-2 min-w-[220px] bg-background text-foreground border border-foreground/15 shadow-2xl"
                                    >
                                        <div className="px-4 py-3 border-b border-border">
                                            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Signed in as</div>
                                            <div className="text-sm truncate">{user.email}</div>
                                        </div>
                                        <button
                                            data-testid="navbar-signout-btn"
                                            onClick={() => { setMenuOpen(false); signOut(); }}
                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-secondary hover:text-secondary-foreground transition-colors"
                                        >
                                            <LogOut className="w-4 h-4" /> Sign out
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                data-testid="navbar-signin-btn"
                                onClick={signIn}
                                className="inline-flex items-center gap-2 border border-background/25 text-background px-4 py-3 text-xs font-mono tracking-[0.2em] uppercase hover:border-accent hover:text-accent transition-colors"
                            >
                                <LogIn className="w-4 h-4" /> Sign in
                            </button>
                        )}
                        <Link
                            to="/contact"
                            data-testid="navbar-quote-cta"
                            className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background hover:text-foreground transition-colors"
                        >
                            Get Connected
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </div>

                    <button
                        data-testid="navbar-mobile-toggle"
                        className="lg:hidden p-2 text-background"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-background/15 bg-foreground" data-testid="navbar-mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            link.external ? (
                                <a
                                    key={link.to}
                                    href={link.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                    className="px-4 py-3 text-sm font-medium uppercase tracking-wider border-l-2 border-transparent text-background/70"
                                >
                                    {link.label}
                                </a>
                            ) : (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    end={link.to === "/"}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                    className={({ isActive }) =>
                                        `px-4 py-3 text-sm font-medium uppercase tracking-wider border-l-2 ${
                                            isActive
                                                ? "border-accent text-background bg-background/10"
                                                : "border-transparent text-background/70"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            )
                        ))}
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-quote-cta"
                            className="mt-3 inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-5 py-3 text-sm font-bold uppercase tracking-wider"
                        >
                            Get Connected
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
